<template>
    <!-- Desktop sidebar - only visible on md and up -->
    <div class="hidden md:flex fixed left-0 top-0 bottom-0 w-60 bg-dark border-r border-white/5 flex-col z-[60]">
        <!-- + New Item CTA -->
        <div class="px-3 pt-3 pb-2 border-b border-white/5">
            <router-link
                to="/item/new"
                class="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all hover:opacity-90"
                style="background: rgba(185,204,181,0.12); color: #B9CCB5; border: 1px solid rgba(185,204,181,0.2);"
            >
                <MdiPlusCircle class="w-4 h-4" />
                {{ $t('common.newItem') }}
            </router-link>
        </div>

        <!-- Navigation items - scrollable -->
        <nav class="flex-1 overflow-y-auto pb-4 pt-2" style="scrollbar-gutter: stable;">
            <!-- Uncategorized items -->
            <ul class="px-3 mb-6">
                <li v-for="item in uncategorizedItems" :key="item.key" class="mb-1">
                    <router-link 
                        :to="item.to" 
                        class="nav-link"
                        :class="{ 'active': item.activeMatch(route.path) }">
                        <component :is="item.icon" class="w-4 h-4 mr-2 shrink-0" :class="item.iconClass" />
                        {{ $t(item.labelKey) }}
                    </router-link>
                </li>
            </ul>
            
            <!-- Operations category -->
            <div v-if="operationsItems.length > 0" class="mb-6">
                <button 
                    @click="toggleCategory('operations')"
                    class="w-full px-3 py-1 text-xs uppercase text-gray-400 font-semibold tracking-wider flex items-center justify-between hover:text-gray-300 transition-colors">
                    <span class="flex items-center gap-1.5">
                        <MdiClipboardList class="w-3.5 h-3.5 text-emerald-400/70" />
                        {{ $t('nav.groups.operations') }}
                    </span>
                    <span class="transition-transform duration-200" :class="{ 'rotate-180': collapsedCategories.operations }">▼</span>
                </button>
                <ul v-show="!collapsedCategories.operations" class="px-3 mt-2">
                    <li v-for="item in operationsItems" :key="item.key" class="mb-1">
                        <router-link 
                            :to="item.to" 
                            class="nav-link"
                            :class="{ 'active': item.activeMatch(route.path) }">
                            <component :is="item.icon" class="w-4 h-4 mr-2 shrink-0" :class="item.iconClass" />
                            {{ $t(item.labelKey) }}
                            <span
                                v-if="item.key === 'checkout' && store.checkoutItemList.length > 0"
                                class="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold leading-none bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 transition-transform duration-300"
                                :class="{ 'scale-125': store.checkoutBadgePulse }"
                            >{{ store.checkoutItemList.length }}</span>
                        </router-link>
                    </li>
                </ul>
            </div>
            
            <!-- Management category -->
            <div v-if="managementItems.length > 0" class="mb-6">
                <button 
                    @click="toggleCategory('management')"
                    class="w-full px-3 py-1 text-xs uppercase text-gray-400 font-semibold tracking-wider flex items-center justify-between hover:text-gray-300 transition-colors">
                    <span class="flex items-center gap-1.5">
                        <MdiDomain class="w-3.5 h-3.5 text-sky-400/70" />
                        {{ $t('nav.groups.management') }}
                    </span>
                    <span class="transition-transform duration-200" :class="{ 'rotate-180': collapsedCategories.management }">▼</span>
                </button>
                <ul v-show="!collapsedCategories.management" class="px-3 mt-2">
                    <li v-for="item in managementItems" :key="item.key" class="mb-1">
                        <router-link 
                            :to="item.to" 
                            class="nav-link"
                            :class="{ 'active': item.activeMatch(route.path) }">
                            <component :is="item.icon" class="w-4 h-4 mr-2 shrink-0" :class="item.iconClass" />
                            {{ $t(item.labelKey) }}
                        </router-link>
                    </li>
                </ul>
            </div>

            <!-- Administration category (tenant_admin / super_admin only) -->
            <div v-if="administrationItems.length > 0" class="mb-6">
                <button 
                    @click="toggleCategory('administration')"
                    class="w-full px-3 py-1 text-xs uppercase text-red-400/70 font-semibold tracking-wider flex items-center justify-between hover:text-red-300 transition-colors">
                    <span class="flex items-center gap-1.5">
                        <MdiShieldAccount class="w-3.5 h-3.5 text-red-400/70" />
                        {{ $t('nav.groups.administration') }}
                    </span>
                    <span class="transition-transform duration-200" :class="{ 'rotate-180': collapsedCategories.administration }">▼</span>
                </button>
                <ul v-show="!collapsedCategories.administration" class="px-3 mt-2">
                    <li v-for="item in administrationItems" :key="item.key" class="mb-1">
                        <router-link 
                            :to="item.to" 
                            class="nav-link"
                            :class="{ 'active': item.activeMatch(route.path) }">
                            <component :is="item.icon" class="w-4 h-4 mr-2 shrink-0" :class="item.iconClass" />
                            {{ $t(item.labelKey) }}
                        </router-link>
                    </li>
                </ul>
            </div>
        </nav>
        
        <!-- Logout button at bottom -->
        <div class="p-3 border-t border-white/5">
            <button 
                @click="logout()" 
                class="w-full text-left px-3 py-2 text-sm text-gray-400 hover:text-red-400 hover:bg-white/5 rounded transition-colors flex items-center gap-2">
                <MdiLogout class="w-4 h-4 shrink-0" />
                {{ $t('common.logOut') }}
            </button>
        </div>
    </div>
</template>

<script>
import { useStore } from '@/stores/store';
import { useRoute } from 'vue-router';
import { computed, reactive, watch } from 'vue';
import { logOutApi } from '@/api/api.js';
import { sideMenuItems } from '@/config/navItems.js';
import { usePermissions } from '@/composables/usePermissions.js';
import { useShellModeStore } from '@/stores/shellMode.js';
import MdiLogout from '~icons/mdi/logout';
import MdiPlusCircle from '~icons/mdi/plus-circle';
import MdiClipboardList from '~icons/mdi/clipboard-list';
import MdiDomain from '~icons/mdi/domain';
import MdiShieldAccount from '~icons/mdi/shield-account';

export default {
    name: 'DesktopSidebar',
    components: {
        MdiLogout,
        MdiPlusCircle,
        MdiClipboardList,
        MdiDomain,
        MdiShieldAccount,
    },
    setup() {
        const route = useRoute();
        const store = useStore();
        const perms = usePermissions();
        
        // Track collapsed state of each category (default: all false = all open)
        const collapsedCategories = reactive({
            operations: false,
            management: false,
            administration: false,
        });

        const shell = useShellModeStore();

        // Filter items by role using requiredPermission key from usePermissions()
        const filteredNavItems = computed(() =>
            sideMenuItems.filter(item => {
                if (item.shellModes?.length && !item.shellModes.includes(shell.mode)) return false;
                if (!item.requiredPermission) return true;
                return perms[item.requiredPermission]?.value ?? false;
            })
        );
        
        const uncategorizedItems = computed(() => 
            filteredNavItems.value.filter(item => item.category === null)
        );
        
        const operationsItems = computed(() => 
            filteredNavItems.value.filter(item => item.category === 'operations')
        );
        
        const managementItems = computed(() => 
            filteredNavItems.value.filter(item => item.category === 'management')
        );

        const administrationItems = computed(() => 
            filteredNavItems.value.filter(item => item.category === 'administration')
        );
        
        const toggleCategory = (category) => {
            collapsedCategories[category] = !collapsedCategories[category];
        };

        // Auto-expand the category containing the currently active route
        const ensureActiveVisible = () => {
            const active = filteredNavItems.value.find(item => item.activeMatch(route.path));
            if (active?.category) {
                collapsedCategories[active.category] = false;
            }
        };
        watch(() => route.path, ensureActiveVisible, { immediate: true });

        return {
            store,
            route,
            uncategorizedItems,
            operationsItems,
            managementItems,
            administrationItems,
            collapsedCategories,
            toggleCategory,
        };
    },
    methods: {
        logout() {
            this.store.currentLocation = null;
            this.store.currentWarehouse = null;
            try { 
                localStorage.removeItem('location'); 
                localStorage.removeItem('warehouse'); 
            } catch (e) {}
            logOutApi();
        },
    },
};
</script>

<style scoped>
/* Modern scrollbar styling */
nav {
    scrollbar-width: thin;
    scrollbar-color: rgba(185, 204, 181, 0.3) transparent;
}

nav::-webkit-scrollbar {
    width: 8px;
}

nav::-webkit-scrollbar-track {
    background: transparent;
}

nav::-webkit-scrollbar-thumb {
    background-color: rgba(185, 204, 181, 0.3);
    border-radius: 4px;
    transition: background-color 0.2s;
}

nav::-webkit-scrollbar-thumb:hover {
    background-color: rgba(185, 204, 181, 0.5);
}

.nav-link {
    display: flex;
    align-items: center;
    padding: 0.625rem 0.75rem;
    font-size: 0.9375rem;
    font-weight: 500;
    color: #e5e7eb;
    text-decoration: none;
    border-radius: 0.375rem;
    transition: all 0.15s;
}

.nav-link:hover {
    color: #B9CCB5;
    background-color: rgba(255, 255, 255, 0.05);
}

.nav-link.active {
    color: #B9CCB5;
    font-weight: 700;
    background-color: rgba(185, 204, 181, 0.1);
}
</style>
